import type { ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'

export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info'

type StatusVariant =
  | 'active' | 'inactive'
  | 'connected' | 'disconnected'
  | 'error' | 'pending' | 'processing' | 'success'

const TONE_CLASS: Record<StatusTone, string> = {
  success: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400',
  danger:  'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
  neutral: 'bg-muted text-muted-foreground border-border',
  info:    'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400',
}

const VARIANT_MAP: Record<StatusVariant, { label: string; tone: StatusTone }> = {
  active:       { label: 'Active',       tone: 'success' },
  inactive:     { label: 'Inactive',     tone: 'neutral' },
  connected:    { label: 'Connected',    tone: 'success' },
  disconnected: { label: 'Disconnected', tone: 'danger'  },
  success:      { label: 'Success',      tone: 'success' },
  processing:   { label: 'Processing',   tone: 'warning' },
  pending:      { label: 'Pending',      tone: 'warning' },
  error:        { label: 'Error',        tone: 'danger'  },
}

/** Heuristic inference of a tone from a free-form status label (e.g. from backend). */
export function inferStatusTone(label: string): StatusTone {
  const l = label.toLowerCase().trim()
  if (/(success|processed|completed|done|ok|active|connected|resolved|approved)/.test(l)) return 'success'
  if (/(error|failed|fail|rejected|disconnected|expired|critical)/.test(l)) return 'danger'
  if (/(processing|in progress|pending|queued|warning|partial|new|waiting)/.test(l)) return 'warning'
  return 'neutral'
}

interface StatusBadgeProps {
  status: boolean | string
  label?: string
  booleanLabels?: [string, string]
  tone?: StatusTone
  className?: string
  /** Shown before the label, so the state is not told by colour alone. Decorative: the label says it. */
  icon?: ReactNode
}

const BASE = 'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border capitalize'

export function StatusBadge({ status, label, booleanLabels, tone, className, icon }: StatusBadgeProps) {
  if (typeof status === 'boolean') {
    const [trueLabel, falseLabel] = booleanLabels ?? ['Active', 'Inactive']
    const resolvedTone = tone ?? (status ? 'success' : 'neutral')
    return (
      <span className={cn(BASE, TONE_CLASS[resolvedTone], className)}>
        {label ?? (status ? trueLabel : falseLabel)}
      </span>
    )
  }

  const config = VARIANT_MAP[status as StatusVariant]
  const resolvedTone = tone ?? config?.tone ?? inferStatusTone(status)
  const resolvedLabel = label ?? config?.label ?? status

  return (
    <span className={cn(BASE, TONE_CLASS[resolvedTone], className)}>
      {icon}
      {resolvedLabel}
    </span>
  )
}
